import { weekOfYear } from '../deprecated';
import { clamp } from '../math';

export type DateTimeComparer = (a: Date, b: Date) => number;

// ================================================================
// Basic

/**
 * Compare the years between the two `Date`s
 */
export const compareYear: DateTimeComparer = (a, b) =>
  a.getFullYear() - b.getFullYear();

/**
 * Compare the months between the two `Date`s.
 */
export const compareMonth: DateTimeComparer = (a, b) =>
  compareYear(a, b) || a.getMonth() - b.getMonth();

/**
 * Compare the dates between the two `Date`s.
 */
export const compareDate: DateTimeComparer = (a, b) =>
  compareMonth(a, b) || a.getDate() - b.getDate();

/**
 * Compare the hours between the two `Date`s.
 */
export const compareHours: DateTimeComparer = (a, b) =>
  compareDate(a, b) || a.getHours() - b.getHours();

/**
 * Compare the minutes between the two `Date`s.
 */
export const compareMinutes: DateTimeComparer = (a, b) =>
  compareHours(a, b) || a.getMinutes() - b.getMinutes();

/**
 * Compare the seconds between the two `Date`s.
 */
export const compareSeconds: DateTimeComparer = (a, b) =>
  compareMinutes(a, b) || a.getSeconds() - b.getSeconds();

/**
 * Compare the milliseconds between the two `Date`s.
 */
export const compareTime: DateTimeComparer = (a, b) =>
  a.getTime() - b.getTime();

/**
 * Compare the milliseconds between the two `Date`s.
 */
export const compareMilliseconds = compareTime;

// ================================================================
// Day of Week

const dayOfWeekComparerMap = Array.from(
  { length: 7 },
  (_, startDay): DateTimeComparer => {
    const resolveDay = (day: number): number => {
      let result = day - startDay;
      if (result < 0) {
        return 7 - result;
      }
      return result;
    };
    return startDay
      ? (a, b) => resolveDay(a.getDay()) - resolveDay(b.getDay())
      : (a, b) => a.getDay() - b.getDay();
  },
);

/**
 * Create a function to compare day of week.
 * @param startDay The start day of week. 0 is Sunday, 1 is Monday ... 6 is Saturday.
 */
export function createCompareDayOfWeek(startDay: number = 0): DateTimeComparer {
  const offset = Math.floor(clamp(startDay, 0, 6));
  return dayOfWeekComparerMap[offset];
}

export const compareDayOfWeek = createCompareDayOfWeek(0);

// ================================================================
// Week of year

const weekOfYearComparerMap = Array.from(
  { length: 7 },
  (_, startDay): DateTimeComparer => (a, b) =>
    weekOfYear(a, startDay, 1970) - weekOfYear(b, startDay, 1970),
);

export function createCompareWeekOfYear(
  startDay: number = 0,
): DateTimeComparer {
  const offset = Math.floor(clamp(startDay, 0, 6));
  return weekOfYearComparerMap[offset];
}

export const compareWeekOfYear = createCompareDayOfWeek(0);

// ================================================================
// Compare via type

export type CompareDateTimeType =
  | 'year'
  | 'month'
  | 'date'
  | 'hours'
  | 'minutes'
  | 'seconds'
  | 'millisecond'
  | 'time'
  | 'day'
  | 'week';

/**
 * @deprecated
 */
export function compareDateTime(
  type: CompareDateTimeType,
  a: Date,
  b: Date,
  startDay: number = 0,
): number {
  switch (type) {
    case 'year':
      return compareYear(a, b);
    case 'month':
      return compareMonth(a, b);
    case 'date':
      return compareDate(a, b);
    case 'hours':
      return compareHours(a, b);
    case 'minutes':
      return compareMinutes(a, b);
    case 'seconds':
      return compareSeconds(a, b);
    case 'day':
      return createCompareDayOfWeek(startDay)(a, b);
    case 'week':
      return createCompareWeekOfYear(startDay)(a, b);
    default:
      return compareTime(a, b);
  }
}

/**
 * Create a date time comparer for specified type.
 */
export function createCompareDateTime(
  type: CompareDateTimeType,
  startDay: number = 0,
): DateTimeComparer {
  switch (type) {
    case 'year':
      return compareYear;
    case 'month':
      return compareMonth;
    case 'date':
      return compareDate;
    case 'hours':
      return compareHours;
    case 'minutes':
      return compareMinutes;
    case 'seconds':
      return compareSeconds;
    case 'day':
      return createCompareDayOfWeek(startDay);
    case 'week':
      return createCompareWeekOfYear(startDay);
    default:
      return compareTime;
  }
}
